import ApiService from '../api/apiService';
import { citiesLondonFix, currenciesList, logoUrl, shortSymbol } from '../common/constants';
import React, { useState, useEffect, useMemo } from 'react';
import LinkToKitco from '../components/Link/LinkToKitco';
import useSwitchThemeModeInWidget from '../hooks/useSwitchThemeModeInWidget';
import clsx from 'clsx';
import { useGoogleAnaliticsHandler } from '../hooks/useGoogleAnaliticsHandler';
import { arrayToMap, arrayToMapBase, getCurrentDateFormatted, getLocalTimesForCities, mapArrayByTimestamp } from '../utils/functions';
import { updateEmbedWidgetConfig } from '../utils/localStorageHelpers';
import useWidgetConfig from '../hooks/useWidgetConfig';
import { humanizePrice } from '../components/utils';

interface KitcoMorningFixWidgetProps {
  config: WidgetConfig; 
}

interface MorningFixList {
  [key: string]: {
    [key: number]: KFDataI[];
  };
}

const KitcoMorningFixWidget = ({config: baseConfig}: KitcoMorningFixWidgetProps) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [listMorningFixDate, setListMorningFixDate] = useState <MorningFixList | null>(null);
  const [localTimes, setLocalTimes] = useState<Record<string, string>>({});
  const [cities, setCities] = useState<string[]>([]);
  const {config} = useWidgetConfig(baseConfig);
  useGoogleAnaliticsHandler('Kitco_Morning_Fix_Widget');
  const {widgetWrapperRef} = useSwitchThemeModeInWidget(config.colorTheme); 
  const isSm = config.width <= 450; 

  const getData = async (metalsParams: string, currency: string, latest: number = 0, period: number, date: string) => {
    try {
      const response = await ApiService.getLFValueNew(metalsParams, currency, latest, date); 
      if(response) {
        mappingLFValues(response, period);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const reorderListHandler = (originalObject: Record<string, KFDataI[]>) => {
    const preferredOrder = ['AU', 'AG', 'PT', 'PD'];
    const reorderedList = Object.fromEntries(
      Object.entries(originalObject)
        .map(([date, metals]) => {
          const orderedMetals: any = {};

          preferredOrder.forEach((key: any) => {
            if (metals[key]) {
              orderedMetals[key] = metals[key];
            }
          });
          return [date, orderedMetals];
        })
    );
    return reorderedList;
  }

  const trimToSomePeriod = (data: Record<string, KFDataI[]>, period: number) => {
    const sortedDates = Object.keys(data).sort(); 
    const latestTwoDates = sortedDates.slice(-period); 
    const trimmed: any = {};
    latestTwoDates.forEach(date => {
      trimmed[date] = data[date];
    });
  
    return trimmed;
  };

  const mappingLFValues = (data: LFNewResponse, period: number) => {
    const lfValues = data.KFValues.KFValue;
    const result = mapArrayByTimestamp(lfValues, 'Timestamp');
    const mappedByTimestamp = trimToSomePeriod(result, period);
    let list:any = {};
    if(Object.keys(mappedByTimestamp).length > 0) {
      Object.entries(mappedByTimestamp).forEach(([key, value]) => {
        list[key] = arrayToMap(value as any[], 'Symbol')
      })
    }
    const reorderedList = reorderListHandler(list);
    setListMorningFixDate(reorderedList);   
  }

  useEffect(() => {
    setSelectedCurrency(config.defaultCurrency || 'USD');
  }, [config.defaultCurrency]); 

  useEffect(() => {
      const listMetals = config.defaultListMetals || ['AU', 'AG', 'PT', 'PD'];
      const metalsParamsString = listMetals.join(',');
      const defaultLocation = config.defaultLocation || ['New York', 'London', 'Hong Kong', 'Mumbai'];
      setCities(defaultLocation);
      const period = config.defaultDaysNumber || 2;
      const date = getCurrentDateFormatted(); 
      getData(metalsParamsString, selectedCurrency, 2, period, date);
      const localTimes = getLocalTimesForCities(citiesLondonFix);
      const myTime = new Date();
      console.log('myTime', myTime);
      console.log('localTimes', localTimes);
      setLocalTimes(localTimes);
  }, [selectedCurrency, config.defaultDaysNumber, config.defaultListMetals, config.defaultLocation]);


  const PriceCell = ({ am, isLast = false }: any) => (
    <td className={clsx("py-2 text-center dark:text-white", !isLast && "border-b", isSm ? 'px-1 text-[12px]' : 'px-1 text-[12px]')}>{am ? humanizePrice(am) : 'N/A'}</td>
  );

  const PriceRow = ({ value, metalSymbol }: {value: any, metalSymbol: string}) => {
    const mappedBySymbol: Record<string, KFDataI>  = useMemo(() => {
      return arrayToMapBase(value, 'City');
    }, [value]);  
    return (  
      <tr className="border-b">
          <td className={clsx("py-2 text-left dark:text-white border-b capitalize font-semibold", isSm ? 'pr-2 text-[12px]' : 'pr-2 text-[12px]')}>
            {shortSymbol[metalSymbol] || ''}
          </td> 
          {cities.includes(citiesLondonFix[0].name) ? <PriceCell am={mappedBySymbol?.[citiesLondonFix?.[0]?.code]?.Value} /> : null}
          {cities.includes(citiesLondonFix[1].name) ? <PriceCell am={mappedBySymbol?.[citiesLondonFix?.[1]?.code]?.Value} /> : null}
          {cities.includes(citiesLondonFix[2].name) ? <PriceCell am={mappedBySymbol?.[citiesLondonFix?.[2]?.code]?.Value} /> : null}
          {cities.includes(citiesLondonFix[3].name) ? <PriceCell am={mappedBySymbol?.[citiesLondonFix?.[3]?.code]?.Value} isLast={metalSymbol === 'PD'} isRight/> : null}
      </tr>
    )
  }

  return (
    <div ref={widgetWrapperRef} className='w-full flex justify-center'> 
      <div className={` border rounded-lg shadow-lg bg-white text-gray-dark dark:bg-gray-dark dark:text-white ${config?.isTransparent ? 'bg-opacity-0' : 'bg-gray-100 text-gray-800'}`} style={{width: !config?.useContainerSize ? `${config.width}px` : '100%'}}>

        <div className={`${!isSm ? 'py-4 px-4' : 'p-2'} w-full flex justify-between items-center h-[40px] bg-gray-dark text-white dark:bg-gray-light_700 dark:text-white px-4`}>
          <div className={clsx("font-bold flex items-center", isSm ? 'text-[14px]' : 'text-[16px]')}>
          <img src={logoUrl.dark} className='h-[22px] w-auto' alt="Kitco Logo"/> 
          <span className='pt-[3px]'> Morning Fix</span>
          </div>
       {/*    <DateNYTime fontSize='12px' isLightMode/> */}
        </div>

        <div className={`${!isSm ? 'px-4 pb-4' : 'p-2'}`}> 
        <div className={clsx('w-full pt-1 flex justify-between items-center', !isSm ? 'space-x-10' : 'space-x-6' )}>
          <div className='flex items-center space-x-2'>
              <div className='lg:w-[80px]'>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => {
                      const value = e.target.value;  
                      updateEmbedWidgetConfig(baseConfig, 'defaultCurrency', value);
                      setSelectedCurrency(value); 
                    }}
                    className="w-full mr-2 lg:mr-2 p-1 my-2 text-[14px] rounded border bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-[35px]"
                  >
                    {currenciesList.map((currency:any) => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
              </div>
              <div className='text-[14px] p-1 px-3 my-2  rounded border bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-[35px] flex items-center justify-center'>
                oz
              </div>
          </div>
          <div className={!isSm ? 'text-[12px]' : 'text-[11px]'}>
            Prices posted at <span className='font-semibold'>10:30 AM</span> local time in each hub
          </div>
        </div>
      
        <table className="w-full mb-4">
          <thead>
            <tr className="border-b">
              <th className={clsx("py-2 text-left dark:text-white", isSm ? 'pr-2 text-[12px] w-[120px]' : 'pr-2 text-[14px] w-[140px]')}>Metal</th>
              {cities.includes(citiesLondonFix[0].name) ?  <th className={clsx("py-2  dark:text-white", isSm ? 'px-2 text-[12px]' : 'px-2-4 text-[14px]')}>
                <div>{citiesLondonFix[0].name}</div>
                <div className='text-[11px] font-normal'>{`(${citiesLondonFix[0].standart_time})`}</div>
              </th> : null}
              {cities.includes(citiesLondonFix[1].name) ? <th className={clsx("py-2  dark:text-white", isSm ? 'px-2 text-[12px]' : 'px-2 text-[14px]')}>
                <div>{citiesLondonFix[1].name}</div>
                <div className='text-[11px] font-normal'>{`(${citiesLondonFix[1].standart_time})`}</div>
              </th> : null}
              {cities.includes(citiesLondonFix[2].name) ? <th className={clsx("py-2  dark:text-white", isSm ? 'px-2 text-[12px]' : 'px-2 text-[14px]')}>
                <div>{citiesLondonFix[2].name}</div>
                <div className='text-[11px] font-normal'>{`(${citiesLondonFix[2].standart_time})`}</div>
              </th> : null}
              {cities.includes(citiesLondonFix[3].name) ? <th className={clsx("py-2  dark:text-white", isSm ? 'text-[12px]' : 'text-[14px]')}>
                <div>{citiesLondonFix[3].name}</div>
                <div className='text-[11px] font-normal'>{`(${citiesLondonFix[3].standart_time})`}</div>
              </th> : null}
            </tr>
          </thead>

            <tbody>
            {listMorningFixDate && Object.keys(listMorningFixDate).length > 0 && Object.entries(listMorningFixDate).reverse().map(([key, metals]) => (
              <React.Fragment key={key}>
                <tr>
                  <td colSpan={5} className="py-1 font-medium text-center text-[12px] border-b bg-slate-100 dark:bg-gray-light_700 ">
                    {new Date(key).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </td>
                </tr>
                {Object.entries(metals).map((el) => {
                  return <PriceRow key={el[0]} value={el[1]} metalSymbol={el[0]}/>
                  })}
              </React.Fragment>
            ))}
                  <tr>
                  <td className={clsx('py-2 text-left dark:text-white bg-slate-100 dark:bg-gray-light_700', isSm ? 'pr-2 text-[10px]' : 'pr-2 text-[12px]')}>
                      Next Kitco 10 am Fix in:
                    </td>
                    {cities.includes(citiesLondonFix[0].name) ? <td className={clsx("py-2 text-center dark:text-white text-[11px] bg-slate-100 dark:bg-gray-light_700")}>{localTimes[citiesLondonFix?.[0]?.name]}</td> : null}
                    {cities.includes(citiesLondonFix[1].name) ? <td className={clsx("py-2 text-center dark:text-white text-[11px] bg-slate-100 dark:bg-gray-light_700")}>{localTimes[citiesLondonFix?.[1]?.name]}</td> : null}
                    {cities.includes(citiesLondonFix[2].name)  ? <td className={clsx("py-2 text-center dark:text-white text-[11px] bg-slate-100 dark:bg-gray-light_700")}>{localTimes[citiesLondonFix?.[2]?.name]}</td> : null}
                    {cities.includes(citiesLondonFix[3].name)  ? <td className={clsx("py-2 text-center dark:text-white text-[11px] bg-slate-100 dark:bg-gray-light_700")}>{localTimes[citiesLondonFix?.[3]?.name]}</td> : null}
                </tr>  
          </tbody>
        </table>
        <LinkToKitco isDarkMode={config.colorTheme === 'dark'} widgetName='London_Fix_Widget'/>
        </div> 
      </div>
    </div>
  );
};

export default KitcoMorningFixWidget;